import { Fragment, useMemo, useState } from 'react';
import { Card, CardHeader, TrendPill } from '../../../components/ui/Card';
import { InfoTip } from '../../../components/ui/InfoTip';
import { Nil, Table, TGroupRow, THead, Th, Td, Tr } from '../../../components/ui/Table';
import { EmptyState, ErrorState, LoadingState } from '../../../components/ui/States';
import { useCsmDeclining, useCsmRoster } from '../../../hooks/useDashboard';
import { SegmentedControl } from '../../../components/ui/Tabs';
import type { CsmDecliningCompany } from '../../../api/dashboard';
import { formatDayMonthTime } from '../../../utils/dateFormat';
import { formatCompact, formatMoney, formatNumber } from '../../../utils/numberFormat';
import { AttioCompanyLink } from './attio';
import { clampDrop } from './pctChange';
import { COMPARISON_TEXT, groupOf, keptDeclineReason, otherSignal, pctText, TOKENS_LABEL, type DeclineTrigger, type GroupId } from './decliningGroups';

export { keptDeclineReason, pctText } from './decliningGroups';

/** "Login spike days counted as a median day: 2" for the hover title, when the detector evened any out. */
function clippedText(c: CsmDecliningCompany): string | null {
  const events = c.clippedDays?.events ?? 0;
  if (events === 0) return null;
  return `Login spike days counted as a median day (60-day look-back): ${events}`;
}

export type { DeclineTrigger } from './decliningGroups';
export type DecliningRow = CsmDecliningCompany & { trigger?: DeclineTrigger };

/**
 * The trigger as sent by the detector; for an older API that doesn't send one,
 * the steepest drop in the severity's own window (approximate on the 7d window,
 * where the 4-week baseline isn't in the payload).
 */
export function triggerOf(c: DecliningRow): DeclineTrigger | null {
  if (c.trigger) return { ...c.trigger, changePct: clampDrop(c.trigger.changePct, c.trigger.current) ?? c.trigger.changePct };
  const candidates: DeclineTrigger[] =
    c.severity === 'confirmed'
      ? [
          { metric: 'events', window: '30d', changePct: c.monthlyEventsChangePct ?? NaN, current: c.events30d, baseline: c.eventsPrior30d },
          { metric: 'tokens', window: '30d', changePct: c.monthlyTokensChangePct ?? NaN, current: c.tokens30d, baseline: c.tokensPrior30d },
        ]
      : [
          { metric: 'events', window: '7d', changePct: c.weeklyEventsChangePct ?? NaN, current: c.events7d, baseline: NaN },
          { metric: 'tokens', window: '7d', changePct: c.weeklyTokensChangePct ?? NaN, current: c.tokens7d, baseline: NaN },
        ];
  const measured = candidates
    .map((t) => ({ ...t, changePct: clampDrop(t.changePct, t.current) ?? t.changePct }))
    .filter((t) => Number.isFinite(t.changePct));
  if (measured.length === 0) return null;
  return measured.reduce((a, b) => (b.changePct < a.changePct ? b : a));
}

const WINDOW_TEXT: Record<DeclineTrigger['window'], { now: string; before: string }> = {
  '7d': { now: 'last 7 days', before: 'weekly avg of the 4 weeks before' },
  '30d': { now: 'last 30 days', before: '30 days before' },
};

function metricValue(metric: DeclineTrigger['metric'], n: number): string {
  if (!Number.isFinite(n)) return 'n/a';
  return metric === 'tokens' ? formatCompact(n) : formatNumber(n);
}

/** "86.2M (6.4M aOS)": where a token total came from, when the API says. */
function tokenSplit(total: number, aos: number | undefined): string {
  return aos === undefined ? formatCompact(total) || '0' : `${formatCompact(total) || '0'} (${formatCompact(aos) || '0'} aOS)`;
}

/** All four comparisons, for the hover title. */
function allComparisons(c: CsmDecliningCompany): string {
  const pct = (v: number | null) => pctText(v);
  return [
    `Login events 7d vs 4-week avg: ${pct(clampDrop(c.weeklyEventsChangePct, c.events7d))}`,
    `Tokens (Classic + aOS) 7d vs 4-week avg: ${pct(clampDrop(c.weeklyTokensChangePct, c.tokens7d))}`,
    `Login events 30d vs prior 30d: ${pct(clampDrop(c.monthlyEventsChangePct, c.events30d))}`,
    `Tokens (Classic + aOS) 30d vs prior 30d: ${pct(clampDrop(c.monthlyTokensChangePct, c.tokens30d))}`,
    `Tokens 30d: ${tokenSplit(c.tokens30d, c.aosTokens30d)} vs ${tokenSplit(c.tokensPrior30d, c.aosTokensPrior30d)} the 30 days before`,
    clippedText(c),
  ]
    .filter(Boolean)
    .join('\n');
}

const GROUPS: { id: GroupId; label: string; dot: string; title?: string }[] = [
  { id: 'confirmed', label: 'Confirmed decline', dot: 'bg-error' },
  { id: 'early_warning', label: 'Early warning', dot: 'bg-warning' },
  {
    id: 'no_contract',
    label: 'On the list, no live contract',
    dot: 'bg-text-tertiary',
    title: 'A Customer on the CSM list that the Finance MRR engine bills nothing for this month (EMIS Health): a usage drop there is not revenue at risk. Check the contract in Odoo, or the list stage in Attio.',
  },
  {
    id: 'watch',
    label: 'Watch: login events down, tokens up',
    dot: 'bg-text-tertiary',
    title: 'Login events dropped past the threshold, but tokens in the same window rose by 25% or more: more work with fewer sign-ins. Not a decline, so no health-score penalty and no CSM alert.',
  },
];

const COLS = 8;

type SortBy = 'drop' | 'mrr';
const SORT_OPTIONS: readonly { id: SortBy; label: string }[] = [
  { id: 'drop', label: 'Steepest drop' },
  { id: 'mrr', label: 'Most MRR' },
];

/** "4 customers · 3 on tokens, 1 on login events · steepest −100%": the triggering metric, never a total that went up. */
function groupSummary(triggers: Array<DeclineTrigger | null>): string {
  const n = triggers.length;
  const known = triggers.filter((t): t is DeclineTrigger => t !== null);
  const onTokens = known.filter((t) => t.metric === 'tokens').length;
  const onEvents = known.length - onTokens;
  const parts = [`${n} ${n === 1 ? 'customer' : 'customers'}`];
  const by = [onTokens > 0 && `${onTokens} on tokens`, onEvents > 0 && `${onEvents} on login events`].filter(Boolean);
  if (by.length > 0) parts.push(by.join(', '));
  if (known.length > 0) parts.push(`steepest ${pctText(Math.min(...known.map((t) => t.changePct)))}`);
  return parts.join(' · ');
}

export function DecliningTab() {
  const declining = useCsmDeclining();
  // MRR per roster entry, from the same Finance MRR engine the roster shows.
  const roster = useCsmRoster(true);
  const mrrByEntry = useMemo(
    () => new Map((roster.data?.rows ?? []).map((r) => [r.entryId, r.mrr ?? null])),
    [roster.data],
  );
  const mrrUnavailable = roster.isError || !!roster.data?.mrr?.error;
  const mrrKnown = !!roster.data && !mrrUnavailable;
  const [sortBy, setSortBy] = useState<SortBy>('drop');
  // Severity is said once per group, not as a red pill on every row.
  const groups = useMemo(() => {
    const rows = [
      ...((declining.data?.companies ?? []) as DecliningRow[]).map((c) => ({ c, watch: false })),
      ...((declining.data?.watch ?? []) as DecliningRow[]).map((c) => ({ c, watch: true })),
    ].map(({ c, watch }) => {
      const mrr = mrrByEntry.get(c.entryId) ?? null;
      return { c, t: triggerOf(c), mrr, group: groupOf(c, { watch, mrrKnown, mrr }) };
    });
    return GROUPS.map((g) => {
      const inGroup = rows
        .filter((r) => r.group === g.id)
        .sort((a, b) =>
          sortBy === 'mrr' ? (b.mrr ?? 0) - (a.mrr ?? 0) : (a.t?.changePct ?? 0) - (b.t?.changePct ?? 0),
        );
      const mrr = inGroup.reduce((n, r) => n + (r.mrr ?? 0), 0);
      return { ...g, rows: inGroup, mrr };
    }).filter((g) => g.rows.length > 0);
  }, [declining.data, mrrByEntry, mrrKnown, sortBy]);
  const flagged = (declining.data?.companies.length ?? 0) + (declining.data?.watch?.length ?? 0);

  return (
    <Card padding="sm">
      <CardHeader
        title="Declining usage"
        description={declining.data?.detectedAt ? `Computed ${formatDayMonthTime(declining.data.detectedAt)}` : undefined}
        action={
          <div className="flex items-center gap-2">
          <SegmentedControl options={SORT_OPTIONS} value={sortBy} onChange={setSortBy} ariaLabel="Rank by" />
          <InfoTip>
            Same detector that docks the CS health score. <span className="font-medium">Early warning</span>: the last 7
            days are at least 40% below the 4-week average. <span className="font-medium">Confirmed</span>: the last 30
            days are at least 30% below the 30 before, on login events or tokens. Hover the signal for all four
            comparisons. Each row shows the comparison that flagged it, with its own now and before values.{' '}
            <span className="font-medium">Tokens</span> are the roster’s figure: Classic + aOS (SEQUESTO staff left out
            of aOS), 30 days ending today, never smoothed, because a big document run is real use.{' '}
            <span className="font-medium">Login spikes</span>: a login day more than 3× the company’s median active day
            counts as a median day, so a one-off bulk login can’t inflate the baseline.{' '}
            <span className="font-medium">MRR</span>: this month, Finance MRR engine.{' '}
            <span className="font-medium">Watch</span>: a login drop while the same window&rsquo;s tokens rose 25% or
            more is not a decline (more work, fewer sign-ins), so it docks no health score and sends no alert.{' '}
            <span className="font-medium">No live contract</span>: a flagged customer the MRR engine bills nothing for
            is listed apart, not as revenue at risk.
          </InfoTip>
          </div>
        }
      />

      {declining.isLoading ? (
        <LoadingState />
      ) : declining.isError ? (
        <ErrorState title="Couldn’t compute the declining list" error={declining.error} onRetry={() => declining.refetch()} />
      ) : !declining.data || flagged === 0 ? (
        <EmptyState
          title="No declining customers"
          description={
            declining.data ? `None of the ${declining.data.scopeCompanies} linked customers shows a usage decline.` : undefined
          }
          compact
        />
      ) : (
        <Table minWidth={720}>
          <THead>
            <tr>
              <Th>Company</Th>
              <Th>CSM</Th>
              <Th title="The comparison that flagged the company. Tokens are always Classic + aOS">
                Trigger metric
                <span className="block text-body-xs font-normal normal-case tracking-normal text-text-tertiary">tokens = Classic + aOS</span>
              </Th>
              <Th align="right">Change</Th>
              <Th align="right">Now · before</Th>
              <Th align="right" title="The other metric over the same window, so login events and tokens are always both shown">
                Other metric
              </Th>
              <Th align="right" title="This month, Finance MRR engine">MRR</Th>
              <Th className="w-10 !pr-0">
                <span className="sr-only">Attio</span>
              </Th>
            </tr>
          </THead>
          <tbody>
            {groups.map((g) => (
              <Fragment key={g.id}>
                <TGroupRow
                  colSpan={COLS}
                  icon={<span className={`h-1.5 w-1.5 rounded-full ${g.dot}`} aria-hidden />}
                  label={g.title ? <span title={g.title} className="cursor-help">{g.label}</span> : g.label}
                  summary={`${groupSummary(g.rows.map((r) => r.t))}${mrrUnavailable ? '' : ` · ${formatMoney(g.mrr)} MRR`}`}
                />
                {g.rows.map(({ c, t, mrr, group }) => {
                  // Watch rows are not a decline: grey pill.
                  const watch = group === 'watch';
                  return (
                  <Tr key={c.entryId}>
                    <Td className="font-medium text-text-primary">{c.companyName ?? c.attioRecordId}</Td>
                    <Td className="whitespace-nowrap text-text-secondary">{c.primaryCsmName ?? <Nil />}</Td>
                    <Td className="text-text-secondary whitespace-nowrap">
                      {t ? (
                        <span title={allComparisons(c)} className="cursor-help">
                          {t.metric === 'tokens' ? TOKENS_LABEL : 'Login events'}, {t.window}
                        </span>
                      ) : (
                        <Nil />
                      )}
                    </Td>
                    <Td align="right">
                      {t ? (
                        <TrendPill trend={t.changePct < 0 ? 'down' : t.changePct > 0 ? 'up' : 'neutral'} tone={watch ? 'neutral' : undefined}>
                          {pctText(t.changePct)}
                        </TrendPill>
                      ) : (
                        <Nil />
                      )}
                    </Td>
                    <Td align="right" className="tabular-nums whitespace-nowrap">
                      {t ? (
                        <span
                          title={[`${WINDOW_TEXT[t.window].now} vs ${WINDOW_TEXT[t.window].before}`, clippedText(c)]
                            .filter(Boolean)
                            .join('\n')}
                        >
                          <span className="text-text-primary">{metricValue(t.metric, t.current)}</span>
                          <span className="text-body-xs text-text-tertiary"> was {metricValue(t.metric, t.baseline)}</span>
                        </span>
                      ) : (
                        <Nil />
                      )}
                    </Td>
                    <Td align="right" className="tabular-nums whitespace-nowrap">
                      {t ? <OtherMetric c={c} t={t} /> : <Nil />}
                    </Td>
                    <Td align="right" className="tabular-nums whitespace-nowrap">
                      {mrrUnavailable ? (
                        <span className="text-text-tertiary" title="The Finance MRR book could not be loaded">unavailable</span>
                      ) : mrr ? (
                        formatMoney(mrr)
                      ) : (
                        <Nil />
                      )}
                    </Td>
                    <Td align="right" className="!pr-0">
                      <AttioCompanyLink recordId={c.attioRecordId} name={c.companyName} />
                    </Td>
                  </Tr>
                  );
                })}
              </Fragment>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

/**
 * The other metric in the same window. When tokens rose but the row is still a
 * decline (Arendt), a second line says which token drop kept it one.
 */
function OtherMetric({ c, t }: { c: CsmDecliningCompany; t: DeclineTrigger }) {
  const o = otherSignal(c, t);
  const kept = keptDeclineReason(c, t);
  if (o.changePct === null) return <Nil />;
  return (
    <>
      <span className="text-text-secondary" title={`${o.label}, ${WINDOW_TEXT[t.window].now} vs ${WINDOW_TEXT[t.window].before}`}>
        {o.label} {pctText(o.changePct)}
        <span className="block text-body-xs text-text-tertiary">{COMPARISON_TEXT[t.window]}</span>
      </span>
      {kept && (
        <span
          className="block cursor-help text-body-xs text-text-tertiary"
          title={`${o.label} ${pctText(o.changePct)} over ${COMPARISON_TEXT[t.window]} would make this a watch, but ${kept}, so the detector keeps it a decline.`}
        >
          Still a decline: {kept}
        </span>
      )}
    </>
  );
}
